import React, { memo, useCallback, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Badge, IconButton, Popover, Tooltip, Typography } from '@mui/material';
import { SecureMessageIcon } from '@sharedCR/coreDesignSystem/icons';
import { TeamChatConnector } from 'app/shared/components/TeamChat';
import { getChannelId } from 'app/shared/components/TeamChat/TeamChat.utils';

import { IconSize } from 'constants/coreDesignSystem';
import useDocumentTitleUnread from 'hooks/useDocumentTitleUnread';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import usePopover from 'hooks/usePopover';

import { getUuid, selectIsSubCompanyUser, selectIsTitleCompanyUser } from 'store/auth/selectors';
import { getMilestonePresentationSettingsAction } from 'store/milestoneDefinitions/actions';
import { fetchChannelsAction } from 'store/teamChat/actions';
import {
  selectTeamChatAllChannels,
  selectTeamChatTotalUnreadCount,
} from 'store/teamChat/selectors';

import HeaderChatPopover from '../HeaderChatPopover';

import {
  HEADER_CHAT_BELL_POPOVER_ID,
  POPOVER_ANCHOR_ORIGIN,
  POPOVER_TRANSFORM_ORIGIN,
} from './HeaderChatBell.constants';
import { badgeStyles, bellButtonStyles } from './HeaderChatBell.styles';
import { HeaderChatBellTestIds } from './HeaderChatBell.testIds';
import { formatUnreadBadge, getTooltipLabel } from './HeaderChatBell.utils';
import useTeamChatNotificationsDispatcher from './hooks/useTeamChatNotificationsDispatcher';
import useTeamChatShortcuts from './hooks/useTeamChatShortcuts';

const HeaderChatBell = () => {
  const dispatch = useDispatch();

  const loggedUserId = useSelector(getUuid);
  // TPO broker (sub-company) users have no team chat or loan milestones — those reads 403 for them and
  // would trip the global forbidden-reload. Hide the bell and skip its fetches for sub-company users.
  const isSubCompanyUser = useSelector(selectIsSubCompanyUser);
  const isTitleCompanyUser = useSelector(selectIsTitleCompanyUser);
  const totalUnread = useSelector(selectTeamChatTotalUnreadCount);
  const channels = useSelector(selectTeamChatAllChannels);

  const bellButtonRef = useRef(null);

  const { anchorEl, onSetAnchorElement, onClearAnchorElement } = usePopover();

  const [initialChannelId, setInitialChannelId] = useState(null);

  const onButtonClick = useCallback(
    e => {
      setInitialChannelId(null);
      onSetAnchorElement(e);
    },
    [onSetAnchorElement],
  );

  const onPopoverClose = useCallback(() => {
    setInitialChannelId(null);
    onClearAnchorElement();
  }, [onClearAnchorElement]);

  const onShortcutOpen = useCallback(() => {
    if (!bellButtonRef.current) {
      return;
    }

    setInitialChannelId(null);
    onSetAnchorElement({ currentTarget: bellButtonRef.current });
  }, [onSetAnchorElement]);

  const onJumpToNextUnread = useCallback(() => {
    if (!bellButtonRef.current) {
      return;
    }

    const nextUnread = channels.find(channel => (channel?.unreadMessageCount ?? 0) > 0);

    setInitialChannelId(nextUnread ? getChannelId(nextUnread) : null);
    onSetAnchorElement({ currentTarget: bellButtonRef.current });
  }, [channels, onSetAnchorElement]);

  useDocumentTitleUnread(totalUnread);

  useTeamChatNotificationsDispatcher({ loggedUserId });

  useTeamChatShortcuts({
    onOpen: onShortcutOpen,
    onJumpToNextUnread,
  });

  useEffectSafeStrictMode(() => {
    if (loggedUserId && !isSubCompanyUser && !isTitleCompanyUser) {
      dispatch(fetchChannelsAction());
      dispatch(getMilestonePresentationSettingsAction());
    }
  }, [dispatch, loggedUserId, isSubCompanyUser, isTitleCompanyUser]);

  if (!loggedUserId || isSubCompanyUser || isTitleCompanyUser) {
    return null;
  }

  const isOpen = !!anchorEl;
  const tooltipLabel = getTooltipLabel(totalUnread);
  const badgeLabel = formatUnreadBadge(totalUnread);

  return (
    <TeamChatConnector>
      <Tooltip title={tooltipLabel}>
        <IconButton
          disableRipple
          aria-controls={isOpen ? HEADER_CHAT_BELL_POPOVER_ID : null}
          aria-expanded={isOpen}
          aria-haspopup="true"
          aria-label={tooltipLabel}
          data-is-active={isOpen}
          data-testid={HeaderChatBellTestIds.BELL}
          ref={bellButtonRef}
          sx={bellButtonStyles}
          onClick={onButtonClick}
        >
          <Badge
            badgeContent={<Typography variant="bold12">{badgeLabel}</Typography>}
            invisible={!totalUnread}
            overlap="circular"
            sx={badgeStyles}
          >
            <SecureMessageIcon size={IconSize[20]} />
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        anchorEl={anchorEl}
        anchorOrigin={POPOVER_ANCHOR_ORIGIN}
        id={HEADER_CHAT_BELL_POPOVER_ID}
        open={isOpen}
        transformOrigin={POPOVER_TRANSFORM_ORIGIN}
        onClose={onPopoverClose}
      >
        <HeaderChatPopover
          initialChannelId={initialChannelId}
          key={initialChannelId ?? 'default'}
          onClose={onPopoverClose}
        />
      </Popover>
    </TeamChatConnector>
  );
};

export default memo(HeaderChatBell);
